import { useState } from "react";
import { monthsText } from "../../lib/duration";
import type { AssetCard, CategoryRow, InsightDocument, ModuleDistribution, Priority, ViewPoint } from "../../api/insights";
import { moduleShortLabel, riskLevelColor, riskLevelLabel } from "../../lib/riskVocabulary";
import { LevelBadge, NAVY } from "./primitives";

/** Sprint 4 cockpit visuals: priority, two views, time window, category heatmap, readiness, next steps, asset cards. */

export const PRIORITY_STYLE: Record<Priority, { bg: string; fg: string; border: string; label: string }> = {
  P1: { bg: "#14213d", fg: "#fff", border: "#14213d", label: "Act now" },
  P2: { bg: "#4a6fa5", fg: "#fff", border: "#4a6fa5", label: "Plan migration" },
  P3: { bg: "#c3d3e8", fg: "#14213d", border: "#9fb5d3", label: "Strengthen" },
  P4: { bg: "#ffffff", fg: "#14213d", border: "#14213d", label: "Monitor" },
};
const PRIORITIES: Priority[] = ["P1", "P2", "P3", "P4"];

export const MODULE_COLOR: Record<string, string> = {
  timeline: "#1f4e79", data_risk: "#3d73b0", supply_chain: "#7fa6d1", migration_feasibility: "#bcd2ea",
  threat: "#4b2a6b", cryptography: "#7d5aa6", compliance: "#c2b1dc",
};
const LIGHT_MODULES = new Set(["supply_chain", "migration_feasibility", "compliance"]);

export function PriorityChip({ p }: { p: Priority | null }) {
  if (!p) return null;
  const s = PRIORITY_STYLE[p];
  return <span className="inline-block text-[10px] font-bold px-1 rounded-sm border" style={{ background: s.bg, color: s.fg, borderColor: s.border }} title={s.label}>{p}</span>;
}

export function PriorityBar({ priorities }: { priorities: Record<Priority, number> }) {
  const total = PRIORITIES.reduce((a, p) => a + priorities[p], 0);
  if (total === 0) return <p className="text-xs text-ink-500">No scored assets.</p>;
  return (
    <div data-testid="priority-bar">
      <div className="flex h-7 text-xs font-semibold">
        {PRIORITIES.filter((p) => priorities[p] > 0).map((p) => (
          <div key={p} className="flex items-center justify-center border" style={{ flex: priorities[p], background: PRIORITY_STYLE[p].bg, color: PRIORITY_STYLE[p].fg, borderColor: PRIORITY_STYLE[p].border }} title={`${p} ${PRIORITY_STYLE[p].label}: ${priorities[p]}`}>
            {priorities[p]}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-500 mt-1.5">
        {PRIORITIES.map((p) => (
          <span key={p} className="flex items-center gap-1"><span className="w-2.5 h-2.5 border" style={{ background: PRIORITY_STYLE[p].bg, borderColor: PRIORITY_STYLE[p].border }} />{p} {PRIORITY_STYLE[p].label} {priorities[p]}</span>
        ))}
      </div>
    </div>
  );
}

/** Baseline (x) against Technical exposure (y), coloured by priority. */
export function ViewsScatter({ points }: { points: ViewPoint[] }) {
  const [hover, setHover] = useState<ViewPoint | null>(null);
  const pts = points.filter((p) => p.baseline !== null && p.exposure !== null);
  if (pts.length === 0) return <p className="text-xs text-ink-500 py-8 text-center">Needs Baseline and Technical exposure scores.</p>;
  const W = 600, H = 340, L = 40, R = 10, T = 10, B = 34;
  const lo = (v: number[]) => Math.max(0, Math.floor((Math.min(...v) - 5) / 10) * 10);
  const hi = (v: number[]) => Math.min(100, Math.ceil((Math.max(...v) + 5) / 10) * 10);
  const xs = pts.map((p) => p.baseline!), ys = pts.map((p) => p.exposure!);
  const [x0, x1, y0, y1] = [lo(xs), hi(xs), lo(ys), hi(ys)];
  const x = (v: number) => L + ((v - x0) / Math.max(1, x1 - x0)) * (W - L - R);
  const y = (v: number) => T + (1 - (v - y0) / Math.max(1, y1 - y0)) * (H - T - B);
  const hx = Math.max(55, x0), hy = Math.max(75, y0);
  const order: Priority[] = ["P4", "P3", "P2", "P1"];
  return (
    <div className="relative" data-testid="views-scatter">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Baseline risk against Technical exposure">
        {hx < x1 && hy < y1 && <rect x={x(hx)} y={T} width={W - R - x(hx)} height={y(hy) - T} fill="#fbe3e1" />}
        <rect x={L} y={T} width={W - L - R} height={H - T - B} fill="none" stroke="#d4d8de" />
        {hx > x0 && hx < x1 && <line x1={x(hx)} x2={x(hx)} y1={T} y2={H - B} stroke="#1c2433" strokeDasharray="4 3" />}
        {hy > y0 && hy < y1 && <line x1={L} x2={W - R} y1={y(hy)} y2={y(hy)} stroke="#1c2433" strokeDasharray="4 3" />}
        {[x0, x1].map((t) => <text key={`x${t}`} x={x(t)} y={H - B + 14} fontSize="10" fill="#5c5c57" textAnchor="middle">{t}</text>)}
        {[y0, y1].map((t) => <text key={`y${t}`} x={L - 5} y={y(t) + 3} fontSize="10" fill="#5c5c57" textAnchor="end">{t}</text>)}
        <text x={(L + W - R) / 2} y={H - 4} fontSize="10.5" fill="#5c5c57" textAnchor="middle">Baseline risk: data, timeline, vendor, migration →</text>
        <text x={11} y={(T + H - B) / 2} fontSize="10.5" fill="#5c5c57" textAnchor="middle" transform={`rotate(-90 11 ${(T + H - B) / 2})`}>Technical exposure →</text>
        {pts.slice().sort((a, b) => order.indexOf(a.priority ?? "P4") - order.indexOf(b.priority ?? "P4")).map((p) => {
          const s = PRIORITY_STYLE[p.priority ?? "P4"];
          return (
            <circle key={p.asset_id} cx={x(p.baseline!)} cy={y(p.exposure!)} r={p.criticality === "Very_high" ? 6.5 : 5} fill={s.bg} stroke={s.border} strokeWidth={1.4}
              strokeDasharray={p.provisional ? "2 2" : undefined} onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)}>
              <title>{`${p.asset_name}: Baseline ${Math.round(p.baseline!)}, Technical exposure ${Math.round(p.exposure!)}${p.provisional ? " (provisional)" : ""}`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-500 mt-1">
        {PRIORITIES.map((p) => <span key={p} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border" style={{ background: PRIORITY_STYLE[p].bg, borderColor: PRIORITY_STYLE[p].border }} />{p} {PRIORITY_STYLE[p].label}</span>)}
        <span>Dashed: exposure provisional (no crypto inventory)</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5" style={{ background: "#fbe3e1" }} />High+ on both</span>
      </div>
      {hover && (
        <div className="absolute top-1 right-1 bg-white border border-ink-200 shadow-sm rounded-sm px-2 py-1 text-[11px] text-ink-700 pointer-events-none">
          <strong>{hover.asset_name}</strong> · {hover.priority} · Baseline {Math.round(hover.baseline!)} · Exposure {Math.round(hover.exposure!)}
        </div>
      )}
    </div>
  );
}

type TimeRow = InsightDocument["time_window"]["rows"][number];

type MarginInputs = Pick<TimeRow, "status" | "lateness" | "vendor_gated" | "decided_by" | "t1" | "m" | "completion" | "s" | "q_years" | "lifetime_kind" | "harvestable" | "operational_overrun" | "lifetime_overrun">;

/**
 * The time margin as the reports print it (backend reports/statements.ts `timeMargin`, Detailed report
 * Section 3): X, D, Y and Z each to one decimal, the margin is the displayed sum less the displayed Z, from
 * the test that decides the result; two decimals only where one would contradict the engine's result.
 * Rounded once from the engine's inputs, never from its already-rounded lateness.
 */
export function timeMargin(t: MarginInputs): string | null {
  if (t.status === "migrated" || t.vendor_gated || t.lateness === null || t.q_years === null) return null;
  const d = t.t1 ?? 0;
  const y = Math.max(0, (t.completion ?? d + (t.m ?? 0)) - d);
  const x = t.s ?? 0;
  const lifeTested = t.harvestable !== false && t.lifetime_kind !== null && t.s !== null;
  const z = t.q_years;
  const opFails = (t.operational_overrun ?? 0) > 0, lifeFails = (t.lifetime_overrun ?? 0) > 0;
  const eq = (parts: number[], fails: boolean, dp: 1 | 2) => {
    const f = 10 ** dp;
    const total = Math.round(parts.map((p) => Math.round(p * f) / f).reduce((a, p) => a + p, 0) * f) / f;
    const m = Math.round((total - Math.round(z * f) / f) * f) / f;
    return { m, ok: fails ? m > 0 : m <= 0 };
  };
  const dp: 1 | 2 = !eq([d, y], opFails, 1).ok || (lifeTested && !eq([x, d, y], lifeFails, 1).ok) ? 2 : 1;
  const late = t.status === "late" && t.lateness > 0;
  const useLife = late ? lifeTested && lifeFails && (t.lifetime_overrun ?? 0) >= t.lateness - 1e-9 : lifeTested;
  return Math.abs(eq(useLife ? [x, d, y] : [d, y], useLife ? lifeFails : opFails, dp).m).toFixed(dp);
}

/** Plain-language time status, the same wording and figure the reports use. */
export function windowLabel(t: MarginInputs): { text: string; tone: "late" | "tight" | "ok" | "muted" } {
  if (t.status === "migrated") return { text: "Migrated", tone: "ok" };
  if (t.vendor_gated) return { text: "No vendor date", tone: "late" };
  if (t.lateness === null) return { text: "—", tone: "muted" };
  const m = timeMargin(t);
  const yrs = (v: string) => `${v} ${Number(v) === 1 ? "yr" : "yrs"}`;
  if (t.status === "late" && t.lateness > 0 && m !== null) return { text: `${yrs(m)} past the planning year`, tone: "late" };
  if (t.lateness >= 0 || m === null || Number(m) === 0) return { text: "No time to spare", tone: "tight" };
  return { text: `${yrs(m)} spare`, tone: t.status === "tight" ? "tight" : "ok" };
}

const NAVY_4 = "#c3d3e8";
const Q_RED = "#b3261e";

/**
 * Time chart (the extended Mosca test): start delay (D) + migration time (Y), then the years the data must stay
 * protected (X) when the lifetime test applies, against Z, the years left to the run's planning year (red mark).
 * Code fields: D = t1, Y = m, X = s, Z = q_years.
 */
export function TimeChart({ tw, deadlineYears, limit = 14 }: { tw: InsightDocument["time_window"]; deadlineYears: number | null; limit?: number }) {
  const focus = tw.rows.filter((r) => r.status === "late" || r.status === "tight");
  const rows = (focus.length > 0 ? focus : tw.rows).slice(0, limit);
  if (rows.length === 0) return <p className="text-xs text-ink-500 py-8 text-center">No scored asset to place on the time chart.</p>;
  const W = 600, L = 180, R = 18, rowH = 18, T = 16, H = T + rows.length * rowH + 28;
  const end = (r: TimeRow) => r.completion + (r.lifetime_kind && r.s ? r.s : 0);
  const maxX = Math.max(12, ...rows.map((r) => Math.min(40, end(r))), ...rows.map((r) => r.q_years ?? 0), deadlineYears ?? 0) + 1;
  const x = (v: number) => L + (Math.min(Math.max(v, 0), maxX) / maxX) * (W - L - R);
  const ticks: number[] = [];
  for (let v = 0; v <= maxX; v += maxX > 24 ? 5 : 2) ticks.push(v);
  return (
    <div data-testid="time-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Start delay (D) plus migration time (Y), and the years data must stay protected (X), against the years left to the planning year (Z)">
        <defs><pattern id="hatchS" width="5" height="5" patternTransform="rotate(45)" patternUnits="userSpaceOnUse"><rect width="5" height="5" fill="#c9d4e6" /><rect width="2.2" height="5" fill="#7f97bf" /></pattern></defs>
        {ticks.map((v) => <g key={v}><line x1={x(v)} x2={x(v)} y1={T - 4} y2={H - 24} stroke="#eef0f4" /><text x={x(v)} y={H - 12} fontSize="10" fill="#5c5c57" textAnchor="middle">{v}</text></g>)}
        <text x={(L + W - R) / 2} y={H - 1} fontSize="10" fill="#5c5c57" textAnchor="middle">years from the run date</text>
        {deadlineYears !== null && deadlineYears > 0 && <g><line x1={x(deadlineYears)} x2={x(deadlineYears)} y1={T - 8} y2={H - 24} stroke={NAVY} strokeDasharray="3 2" /><text x={x(deadlineYears) + 2} y={T - 7} fontSize="9.5" fill={NAVY}>Deadline</text></g>}
        {rows.map((r, i) => {
          const yy = T + i * rowH;
          const late = r.status === "late";
          const w = windowLabel(r);
          return (
            <g key={r.asset_id}>
              <title>{`${r.asset_name}: start delay (D) ${Math.round(r.t1 * 10) / 10} yrs, migration time (Y) ${r.m === null ? "—" : monthsText(r.m)}${r.s !== null ? `, must stay protected (X) ${r.s} yrs` : ""}; Z = ${r.q_years === null ? "—" : Math.round(r.q_years * 10) / 10} yrs to the planning year ${r.q_year ?? "—"} · ${w.text}`}</title>
              <text x={L - 6} y={yy + 12} fontSize="10.5" textAnchor="end" fill={late ? Q_RED : "#2e2e2c"} fontWeight={late ? 700 : 400}>{r.asset_name.length > 28 ? `${r.asset_name.slice(0, 27)}…` : r.asset_name}</text>
              {r.t1 > 0 && <rect x={x(0)} y={yy + 3} width={x(r.t1) - x(0)} height={rowH - 7} fill={NAVY_4} />}
              <rect x={x(r.t1)} y={yy + 3} width={Math.max(1, x(r.completion) - x(r.t1))} height={rowH - 7} fill={NAVY} />
              {r.lifetime_kind && r.s ? <rect x={x(r.completion)} y={yy + 3} width={Math.max(1, x(r.completion + r.s) - x(r.completion))} height={rowH - 7} fill="url(#hatchS)" /> : null}
              {r.q_years !== null && <line x1={x(r.q_years)} x2={x(r.q_years)} y1={yy + 1} y2={yy + rowH - 2} stroke={Q_RED} strokeWidth={2} />}
              {end(r) > maxX && <text x={W - R + 2} y={yy + 12} fontSize="9" fill="#5c5c57">{Math.round(end(r))}</text>}
              {r.vendor_gated && end(r) <= maxX - 3 && <text x={x(end(r)) + 4} y={yy + 12} fontSize="9" fill={Q_RED} fontWeight={600}>no vendor date</text>}
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-500 mt-1">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5" style={{ background: NAVY_4 }} />Start delay (D)</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5" style={{ background: NAVY }} />Migration time (Y)</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5" style={{ background: "repeating-linear-gradient(45deg,#c9d4e6,#c9d4e6 2px,#7f97bf 2px,#7f97bf 4px)" }} />Must stay protected (X)</span>
        <span className="flex items-center gap-1"><span className="w-1 h-2.5" style={{ background: Q_RED }} />CRQC planning year (Z years away)</span>
        {focus.length > rows.length && <span>+{focus.length - rows.length} more out of time or tight</span>}
        {focus.length === 0 && <span>No asset is out of time or tight; showing the closest.</span>}
        {rows.some((r) => r.vendor_gated) && <span><span style={{ color: Q_RED }}>no vendor date</span>: out of time until the vendor commits to a PQC date, whatever the estimate</span>}
      </div>
    </div>
  );
}

const oneIn = (pct: number) => `1 in ${Math.round(100 / pct)}`;

/** The planning year label: "2035 (expert curve at 1 in 3)". */
export function planningBasisText(py: InsightDocument["planning_year"]): string {
  return py.basis === "project_fixed" ? "fixed for this project"
    : py.basis === "curve_at_tolerance" ? `expert curve at ${oneIn(py.tolerance_pct)}`
      : "default; the expert curve is empty";
}

/** The run's one planning year, what another year would give, and how the answer moves with the curve's spread. */
export function PlanningYearTables({ doc }: { doc: Pick<InsightDocument, "planning_year" | "planning_year_options" | "time_window"> }) {
  const th = "py-1 text-[10px] uppercase tracking-wider text-ink-500";
  const tw = doc.time_window;
  const py = doc.planning_year;
  const opts = doc.planning_year_options.filter((o) => o.year !== null);
  return (
    <div className="space-y-3 text-xs" data-testid="planning-year-tables">
      <p data-testid="planning-year-line"><span className="font-semibold tabular-nums">CRQC planning year {py.year}</span> <span className="text-ink-500">({planningBasisText(py)}), for every asset.</span></p>
      {opts.length === 0 && (
        <p className="text-[11px] text-ink-500" data-testid="no-planning-year-options">
          {py.basis === "project_fixed"
            ? `A fixed planning year (${py.year}) applies to every asset, so the spread of expert estimates does not change the answer.`
            : `No CRQC probability curve is loaded, so every asset uses the default planning year ${py.year}. Load the curve to compare years.`}
        </p>
      )}
      {opts.length > 0 && (
      <table className="w-full" data-testid="planning-year-options">
        <thead><tr className="text-left"><th className={th}>What if another CRQC planning year</th><th className={`${th} text-right`}>P1</th><th className={`${th} text-right`}>Out of time</th></tr></thead>
        <tbody>{opts.map((o) => (
          <tr key={o.tolerance_pct} className={`border-t border-ink-50 ${o.current ? "font-semibold" : ""}`}><td className="py-1">{o.year} <span className="text-ink-400 font-normal">({o.label}){o.current ? " · in use" : ""}</span></td><td className="py-1 text-right tabular-nums">{o.p1}</td><td className="py-1 text-right tabular-nums" style={{ color: (o.late ?? 0) > 0 ? Q_RED : undefined }}>{o.late}</td></tr>
        ))}</tbody>
      </table>
      )}
      {tw.scenarios.length > 0 && (
      <table className="w-full" data-testid="scenarios">
        <thead><tr className="text-left"><th className={th}>If the experts are wrong</th><th className={`${th} text-right`}>P1</th><th className={`${th} text-right`}>Out of time</th></tr></thead>
        <tbody>{tw.scenarios.map((s) => (
          <tr key={s.edge} className={`border-t border-ink-50 ${s.edge === "avg" ? "font-semibold" : ""}`}><td className="py-1">{s.label}{s.year !== null ? ` (${s.year})` : ""}</td><td className="py-1 text-right tabular-nums">{s.p1}</td><td className="py-1 text-right tabular-nums" style={{ color: s.late > 0 ? Q_RED : undefined }}>{s.late}</td></tr>
        ))}</tbody>
      </table>
      )}
      {tw.curve_source && <p className="text-[10.5px] text-ink-400">{tw.curve_source.split(";")[0]}.</p>}
    </div>
  );
}

const SHORT: Record<string, string> = { Critical: "Crit.", Very_high: "V.high", High: "High", Moderate: "Mod.", Low: "Low", Very_low: "V.low", No_risk: "None" };
const LEAVES = ["timeline", "data_risk", "supply_chain", "migration_feasibility", "threat", "cryptography", "compliance"];

export function CategoryHeat({ categories }: { categories: CategoryRow[] }) {
  if (categories.length === 0) return <p className="text-xs text-ink-500">No asset categories.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="category-heat">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-ink-500">
            <th className="text-left py-1.5 pr-2">Category</th>
            {LEAVES.map((m) => <th key={m} className="px-0.5 py-1.5 text-center">{moduleShortLabel(m)}</th>)}
            <th className="px-1 py-1.5 text-center">Priority</th>
            <th className="text-left py-1.5 pl-2">What drives it</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((c) => (
            <tr key={c.category} className="border-t border-ink-50">
              <td className="py-1 pr-2"><div className="font-medium text-ink-900 whitespace-nowrap">{c.category}</div><div className="text-[10.5px] text-ink-500">{c.assets} {c.assets === 1 ? "asset" : "assets"}{c.vendors.length > 0 && ` · ${c.vendors.slice(0, 2).join(", ")}${c.vendors.length > 2 ? "…" : ""}`}</div></td>
              {LEAVES.map((m) => {
                const v = c.cells[m];
                if (!v) {
                  const gap = c.missing?.[m] === "data_gap";
                  return <td key={m} className="px-0.5 py-0.5"><div className="text-center text-[10px] text-ink-400 bg-ink-50 rounded-sm py-1.5" title={gap ? "Data gap: the dimension applies but an input is missing" : "Not applicable to these assets"} data-missing={gap ? "data_gap" : "not_applicable"}>{gap ? "Data gap" : "N/A"}</div></td>;
                }
                const col = riskLevelColor(v.level ?? "Moderate");
                return <td key={m} className="px-0.5 py-0.5"><div className="text-center text-[10px] font-semibold rounded-sm py-1" style={{ background: `${col}22`, color: col }} title={`${moduleShortLabel(m)} worst: ${riskLevelLabel(v.level ?? "")} ${Math.round(v.score)}`}>{SHORT[v.level ?? ""] ?? "—"}<span className="block font-normal">{Math.round(v.score)}</span></div></td>;
              })}
              <td className="px-1 py-1 text-center whitespace-nowrap space-x-0.5">{(["P1", "P2", "P3", "P4"] as Priority[]).filter((p) => c.priorities[p] > 0).map((p) => <span key={p} className="inline-block text-[10px] font-bold px-1 border rounded-sm" style={{ background: PRIORITY_STYLE[p].bg, color: PRIORITY_STYLE[p].fg, borderColor: PRIORITY_STYLE[p].border }} title={`${p} ${PRIORITY_STYLE[p].label}`}>{c.priorities[p]}</span>)}</td>
              <td className="py-1 pl-2 text-[11px] text-ink-600">{c.drivers.join("; ") || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const DIST_ORDER = ["Critical", "Very_high", "High", "Moderate", "Low", "Very_low", "No_risk"];

export function ModuleDistributionTable({ rows }: { rows: ModuleDistribution[] }) {
  const name = (c: string) => (c === "exposure" ? "Technical exposure" : c === "integrated_foundation" ? "Baseline" : c === "integrated_advanced" ? "Overall" : moduleShortLabel(c));
  return (
    <table className="w-full text-xs" data-testid="module-distribution">
      <thead><tr className="text-[10px] uppercase tracking-wider text-ink-500 text-left"><th className="py-1">Module</th><th className="py-1 text-right pr-2">Avg</th><th className="py-1">Assets by level</th><th className="py-1 text-right">High+</th></tr></thead>
      <tbody>
        {rows.map((m) => {
          const total = m.scored + m.na;
          const strong = m.module_code.startsWith("integrated") || m.module_code === "exposure";
          return (
            <tr key={m.module_code} className={`border-t border-ink-50 ${strong ? "font-semibold" : ""}`}>
              <td className="py-1 whitespace-nowrap"><span className="inline-block w-2 h-2 mr-1.5" style={{ background: MODULE_COLOR[m.module_code] ?? "transparent" }} />{name(m.module_code)}</td>
              <td className="py-1 text-right pr-2 tabular-nums">{m.mean ?? "—"}</td>
              <td className="py-1 w-1/2">
                <div className="flex h-3.5 rounded-sm overflow-hidden">
                  {DIST_ORDER.filter((l) => (m.levels[l] ?? 0) > 0).map((l) => <div key={l} style={{ flex: m.levels[l], background: riskLevelColor(l) }} title={`${riskLevelLabel(l)}: ${m.levels[l]}`} />)}
                  {m.na > 0 && <div style={{ flex: m.na, background: "#e2e8f0" }} title={`N/A: ${m.na}`} />}
                  {total === 0 && <div className="flex-1 bg-ink-50" />}
                </div>
              </td>
              <td className="py-1 text-right tabular-nums">{m.high_plus}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ReadinessPanel({ readiness }: { readiness: InsightDocument["readiness"] }) {
  // Only a verified review, frozen with the run (Step 5); anything else is "not yet assessed".
  if (!readiness || readiness.overall_avg === null) return <p className="text-xs text-ink-500" data-testid="readiness-not-assessed">Organisational readiness: not yet assessed. Answer and verify the 25 items on the Organisational readiness tab, then start a new run, to see where the programme may stall.</p>;
  const asOf = readiness.as_of ? new Date(readiness.as_of).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;
  return (
    <div data-testid="readiness">
      <p className="text-xs text-ink-500 mb-2">Overall {readiness.overall_avg.toFixed(1)} of 4 · {readiness.answered} of {readiness.total} answered{asOf && ` · as of ${asOf} (verified)`}. 1 Not started · 2 Started · 3 Largely in place · 4 Fully in place.</p>
      <ul className="space-y-1.5">
        {readiness.areas.map((a) => {
          const v = a.avg ?? 0;
          const col = v < 2 ? riskLevelColor("Very_high") : v < 3 ? riskLevelColor("Moderate") : riskLevelColor("Very_low");
          return (
            <li key={a.category} className="grid grid-cols-[1fr_120px_36px] gap-2 items-center text-xs" title={a.weakest ? `Weakest item: ${a.weakest.area} (${a.weakest.level} of 4)` : undefined}>
              <span className="truncate text-ink-800">{a.category}</span>
              <div className="h-2 bg-ink-50 rounded-sm"><div className="h-full rounded-sm" style={{ width: `${(v / 4) * 100}%`, background: col }} /></div>
              <span className="tabular-nums text-right text-ink-600">{a.avg == null ? "—" : a.avg.toFixed(1)}</span>
            </li>
          );
        })}
      </ul>
      {readiness.not_started.length > 0 && (
        <p className="text-[11px] text-ink-500 mt-2">Items not started: {readiness.not_started.slice(0, 5).map((n) => n.area).join(" · ")}{readiness.not_started.length > 5 && ` · +${readiness.not_started.length - 5} more`}</p>
      )}
    </div>
  );
}

export function NextStepsPanel({ next }: { next: InsightDocument["next_steps"] }) {
  const list = (title: string, xs: InsightDocument["next_steps"]["first_90_days"], start: number) => (
    <div>
      <h4 className="text-[10px] uppercase tracking-wider text-ink-500 mb-1">{title}</h4>
      <ol className="space-y-1.5" start={start}>
        {xs.map((x, i) => <li key={x.title} className="text-xs flex gap-2" title={x.detail}><span className="text-ink-400 tabular-nums w-3">{start + i}</span><span><span className="font-medium text-ink-900">{x.title}.</span> <span className="text-ink-600">{x.detail}</span></span></li>)}
      </ol>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="next-steps">
      <div className="grid grid-cols-2 gap-4">
        {list("First 90 days", next.first_90_days, 1)}
        {list("Within 12 months", next.within_12_months, next.first_90_days.length + 1)}
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        {next.measures.map((m) => (
          <div key={m.label} className="border border-ink-100 rounded-sm px-2.5 py-1.5"><div className="text-[10px] text-ink-500">{m.label}</div><div className="text-sm font-semibold tabular-nums" style={{ color: NAVY }}>{m.now}</div></div>
        ))}
      </div>
    </div>
  );
}

function Segments({ segments, total }: { segments: Array<{ module_code: string; points: number }>; total: number | null }) {
  return (
    <div className="relative h-5 bg-ink-50 rounded-sm flex text-[10px] font-semibold">
      {segments.map((s) => (
        <div key={s.module_code} className="flex items-center justify-center" style={{ width: `${s.points}%`, background: MODULE_COLOR[s.module_code], color: LIGHT_MODULES.has(s.module_code) ? "#14213d" : "#fff" }} title={`${moduleShortLabel(s.module_code)} ${Math.round(s.points)}`}>
          {s.points >= 6 ? Math.round(s.points) : ""}
        </div>
      ))}
      {total != null && <span className="ml-1 self-center text-xs font-bold" style={{ color: NAVY }}>{Math.round(total)}</span>}
      {[55, 75, 90].map((t) => <span key={t} className="absolute top-0 bottom-0 border-l border-dashed border-ink-700" style={{ left: `${t}%` }} />)}
    </div>
  );
}

function CardTime({ card }: { card: AssetCard }) {
  const t = card.time;
  const w = windowLabel(t);
  const color = w.tone === "late" ? Q_RED : w.tone === "tight" ? riskLevelColor("Moderate") : undefined;
  const test = t.decided_by === "lifetime" ? (t.lifetime_kind === "signature_trust" ? "signatures must stay trusted" : "data must stay confidential") : t.decided_by === "operational" ? "migration finishes" : t.decided_by === "vendor" ? "the vendor has no date" : null;
  return (
    <div className="mt-2 text-ink-600 space-y-0.5" data-testid="card-time">
      <p>
        {t.t1_source === "in_progress" ? "Under way" : `Start delay (D) ${Math.round(t.t1 * 10) / 10} yr${t.t1_source === "not_planned" ? " (not planned)" : ""}`} · {t.t1_source === "in_progress" ? "left to run (Y)" : "migration time (Y)"} {t.m} yr{t.m === 1 ? "" : "s"}{t.m_source === "default" ? " (default)" : ""}{t.assumed.includes("migration_overran") ? " (estimate overran; assumed)" : ""}
        {t.s !== null && <> · must stay protected (X) {t.s} yr{t.s === 1 ? "" : "s"}{t.s_source === "shelf_life" ? " (shelf life)" : ""}</>}
        {" "}vs planning year {t.q_year ?? "—"}{t.q_years !== null && <> (Z = {Math.round(t.q_years * 10) / 10} yrs)</>}
      </p>
      <p><strong style={{ color }}>{w.text}</strong>{test && w.tone !== "ok" && w.tone !== "muted" && <span className="text-ink-500"> · decided by when {test}</span>}{t.overdue && <span style={{ color: Q_RED }}> · data already outlasts the planning year</span>}{t.regulatory_miss && <span style={{ color: Q_RED }}> · misses the deadline</span>}</p>
      {card.fix_now && <p><span className="inline-block text-[10px] font-bold px-1 rounded-sm text-white" style={{ background: Q_RED }}>Fix now</span> <span className="text-ink-500">Broken today, not only by a quantum computer.</span></p>}
    </div>
  );
}

/** Expanded register row: how the score was built and the evidence behind it. */
export function AssetCardView({ card }: { card: AssetCard }) {
  return (
    <div className="grid grid-cols-3 gap-5 text-xs" data-testid={`card-${card.asset_id}`}>
      <div>
        <h5 className="text-[10px] uppercase tracking-wider text-ink-500 mb-1">Score by module</h5>
        <ul className="space-y-0.5">
          {card.modules.map((m) => (
            <li key={m.module_code} className="flex justify-between items-center">
              <span><span className="inline-block w-2 h-2 mr-1.5" style={{ background: MODULE_COLOR[m.module_code] }} />{moduleShortLabel(m.module_code)}</span>
              {m.level === "NA" ? <span className="text-[10px] text-ink-400">{m.na_kind === "data_gap" ? "Not assessed" : "N/A"}</span> : <LevelBadge level={m.level} score={m.score} />}
            </li>
          ))}
        </ul>
        <CardTime card={card} />
      </div>
      <div className="space-y-1.5">
        <h5 className="text-[10px] uppercase tracking-wider text-ink-500">What the risk is</h5>
        <p className="text-ink-700">{card.summary}</p>
        <h5 className="text-[10px] uppercase tracking-wider text-ink-500 pt-1">Why this score</h5>
        <div className="text-[11px] text-ink-500">Baseline {Math.round(card.baseline.score ?? 0)}</div>
        <Segments segments={card.baseline.segments} total={card.baseline.score} />
        {card.largest_inputs.length > 0 && <p className="text-[11px] text-ink-500">Largest inputs: {card.largest_inputs.map((i) => `${i.label} ${i.value}`).join(", ")}</p>}
        <div className="text-[11px] text-ink-500">Technical exposure {Math.round(card.exposure_view.score ?? 0)}{card.exposure_view.provisional && " (provisional)"}</div>
        <Segments segments={card.exposure_view.segments} total={card.exposure_view.score} />
      </div>
      <div className="space-y-1">
        <h5 className="text-[10px] uppercase tracking-wider text-ink-500">Evidence behind it</h5>
        {card.evidence.total === 0 ? <p className="text-ink-500">No cryptographic inventory: Cryptography and Compliance are not assessed, not safe.</p> : (
          card.evidence.top.map((t) => (
            <div key={t.label}><LevelBadge level={t.risk_level} /> <span className="font-medium">{t.finding ?? `${t.algorithm ?? ""} ${t.artifact_type}`}</span> <span className="text-ink-400">{t.label}</span>{t.citation && <div className="text-[10.5px] text-ink-500">{t.citation}</div>}</div>
          ))
        )}
        {card.threat ? <p className="text-ink-600 pt-1"><strong>Threat scenario:</strong> {card.threat.threat_event}{card.threat.impact && ` → ${card.threat.impact}`}</p> : <p className="text-ink-400 pt-1">No post-quantum threat scenario modelled for this asset type.</p>}
        <p className="text-ink-600"><strong>Non-compliant standards.</strong> {card.standards.current.length > 0 && `Today: ${card.standards.current.join(", ")}. `}{card.standards.target.length > 0 && `Target: ${card.standards.target.join(", ")}.`}{card.standards.current.length + card.standards.target.length === 0 && "None recorded."}</p>
      </div>
    </div>
  );
}
